import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { CampaignEditor } from '../components/CampaignEditor';
import { FocusHeader } from '../components/FocusHeader';
import { useAutoSave } from '../hooks/useAutoSave';
import { Eye, AlertCircle, User, Info } from 'lucide-react';
import { InsertFirstNameModal } from '../components/InsertFirstNameModal';
import { TestEmailModal } from '../components/TestEmailModal';
import { ToastContainer } from '../components/Toast';
import { generateEmailFooter } from '../lib/emailFooter';
import { sendTestEmail } from '../lib/testEmailHelper';

export function ManualCampaignCreate() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [fromName, setFromName] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [showFirstNameModal, setShowFirstNameModal] = useState(false);
  const [firstNameInsertTarget, setFirstNameInsertTarget] = useState<'subject' | 'content'>('subject');
  const [showTestModal, setShowTestModal] = useState(false);
  const [subjectError, setSubjectError] = useState('');
  const [subjectTouched, setSubjectTouched] = useState(false);
  const [toasts, setToasts] = useState<Array<{ id: string; type: 'success' | 'error' | 'warning' | 'info'; title: string; message?: string; duration?: number }>>([]);

  useEffect(() => {
    if (user) {
      initializeCampaign();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  const initializeCampaign = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { data: userData } = await supabase
        .from('users')
        .select('first_name, sendgrid_from_name')
        .eq('id', user.id)
        .single();

      if (userData) {
        const defaultFromName = userData.sendgrid_from_name || userData.first_name || user.email;
        setFromName(defaultFromName);
      }
    } catch (err) {
      console.error('Error initializing campaign:', err);
      alert('Er ging iets mis bij het aanmaken van de campaign');
      navigate('/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const saveCampaign = async () => {
    if (!user) return;
    if (!subject.trim() && !content.trim()) return;

    if (!campaignId) {
      const { data: campaign, error: insertError } = await supabase
        .from('email_campaigns')
        .insert({
          user_id: user.id,
          blog_id: null,
          subject,
          content,
          from_name: fromName,
          status: 'draft',
        })
        .select()
        .single();

      if (insertError) throw insertError;
      setCampaignId(campaign.id);
      return;
    }

    const { error } = await supabase
      .from('email_campaigns')
      .update({
        subject,
        content,
        from_name: fromName,
      })
      .eq('id', campaignId);

    if (error) {
      throw error;
    }
  };

  const { isSaving, lastSaved, saveNow } = useAutoSave({
    data: { subject, content, fromName },
    onSave: saveCampaign,
    debounceMs: 8000,
    enabled: !!user && (!!subject.trim() || !!content.trim()),
  });

  const addToast = (type: 'success' | 'error' | 'warning' | 'info', title: string, message?: string) => {
    const id = Math.random().toString(36).substring(7);
    setToasts((prev) => [...prev, { id, type, title, message }]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const handleSendTest = async (email: string, name: string) => {
    if (!user || !subject || !content) return;

    await sendTestEmail({
      userId: user.id,
      toEmail: email,
      toName: name,
      subject,
      content,
    });

    addToast('success', 'Test email verzonden!', `De test email is succesvol verzonden naar ${email}`);
  };

  const handleInsertFirstName = (fallback: string) => {
    const placeholder = `{{first_name|${fallback}}}`;

    if (firstNameInsertTarget === 'subject') {
      setSubject((prev) => prev + placeholder);
    } else {
      const editor = document.querySelector('.tiptap');
      if (editor) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(placeholder));
        }
      }
      setContent((prev) => prev + placeholder);
    }
  };

  const handleFinish = async () => {
    if (!subject.trim()) {
      setSubjectError('Subject is verplicht om de campaign op te slaan');
      setSubjectTouched(true);
      return;
    }
    if (!content.trim()) {
      alert('Vul email content in voordat je klaar bent');
      return;
    }

    if (hasUnsavedChanges) {
      try {
        await saveNow();
        navigate('/campaigns');
      } catch (error) {
        console.error('Save failed:', error);
        addToast('error', 'Kon niet opslaan', 'Probeer het opnieuw');
        return;
      }
    } else {
      navigate('/campaigns');
    }
  };

  const validateSubject = () => {
    if (subjectTouched && !subject.trim()) {
      setSubjectError('Subject is verplicht');
      return false;
    } else {
      setSubjectError('');
      return true;
    }
  };

  const handleSubjectChange = (value: string) => {
    setSubject(value);
    if (subjectTouched || value.trim()) {
      setSubjectTouched(true);
      if (!value.trim()) {
        setSubjectError('Subject is verplicht');
      } else {
        setSubjectError('');
      }
    }
  };

  const handleSubjectBlur = () => {
    setSubjectTouched(true);
    validateSubject();
    if (subject.trim()) {
      saveNow();
    }
  };

  const handleTestClick = () => {
    if (!subject.trim()) {
      setSubjectError('Vul eerst een subject in voordat je een test email verstuurt');
      setSubjectTouched(true);
      return;
    }
    setShowTestModal(true);
  };

  const isFormValid = subject.trim() && content.trim();

  const hasBusinessData = user && user.address && user.city && user.postal_code;

  const footerPreview = user && hasBusinessData
    ? generateEmailFooter({
        businessData: {
          company_name: user.company_name,
          address: user.address,
          city: user.city,
          postal_code: user.postal_code,
          country: user.country || 'Nederland',
        },
        unsubscribeUrl: '#',
        recipientEmail: 'voorbeeld@email.nl',
      })
    : null;

  const hasUnsavedChanges = !lastSaved && (subject.trim() || content.trim());

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <LoadingSpinner text="Campaign aanmaken..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title="Nieuwe Campaign Maken"
        onBack={() => navigate('/campaigns/create')}
        customActions={
          <div className="flex items-center gap-2 text-sm min-w-[120px]">
            {hasUnsavedChanges ? (
              <>
                <div className="w-2 h-2 bg-amber-600 rounded-full" />
                <span className="text-amber-600">Niet opgeslagen</span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 bg-green-600 rounded-full" />
                <span className="text-green-600">Opgeslagen</span>
              </>
            )}
          </div>
        }
      />

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-wrap items-center justify-end gap-3 pb-4">
          <button
            onClick={handleTestClick}
            disabled={!subject || !content}
            className="btn-secondary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            title={!isFormValid ? 'Vul subject en content in om een test email te versturen' : ''}
          >
            <Eye className="w-5 h-5" />
            Test
          </button>
          <button
            onClick={() => navigate('/campaigns')}
            className="btn-secondary inline-flex items-center gap-2"
          >
            Annuleer
          </button>
          <button
            onClick={handleFinish}
            disabled={isSaving || !isFormValid}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Opslaan...' : 'Klaar'}
          </button>
        </div>

        <InsertFirstNameModal
          isOpen={showFirstNameModal}
          onClose={() => setShowFirstNameModal(false)}
          onInsert={handleInsertFirstName}
        />

        <TestEmailModal
          isOpen={showTestModal}
          onClose={() => setShowTestModal(false)}
          onSend={handleSendTest}
          defaultEmail={user?.email || ''}
          defaultName={user?.first_name || ''}
        />

        <div className="card space-y-6">
          {!hasBusinessData && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900 mb-1">
                    Email Footer Gegevens Ontbreken
                  </h3>
                  <p className="text-sm text-amber-800">
                    Je bedrijfsgegevens (adres, stad, postcode) zijn niet compleet. Deze zijn wettelijk verplicht voor email campaigns.
                    <a href="/dashboard/settings" className="underline ml-1">Vul je gegevens aan in de instellingen</a>.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Van Naam
            </label>
            <input
              type="text"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              onBlur={saveNow}
              className="input"
              placeholder="Jouw naam of bedrijfsnaam"
            />
            <p className="text-xs text-gray-500 mt-1">
              Deze naam verschijnt als afzender in de inbox van je ontvangers
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Subject Line <span className="text-red-500">*</span>
              </label>
              <button
                onClick={() => {
                  setFirstNameInsertTarget('subject');
                  setShowFirstNameModal(true);
                }}
                className="text-xs btn-secondary inline-flex items-center gap-1 py-1 px-2"
                title="Voeg gepersonaliseerde voornaam toe"
              >
                <User className="w-3 h-3" />
                Voeg Voornaam Toe
              </button>
            </div>
            <div className="relative">
              <textarea
                value={subject}
                onChange={(e) => handleSubjectChange(e.target.value)}
                onBlur={handleSubjectBlur}
                rows={2}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent outline-none resize-y overflow-wrap break-word ${
                  subjectError
                    ? 'border-red-300 focus:ring-red-200 bg-red-50'
                    : 'border-gray-300 focus:ring-[#FF6B35]'
                }`}
                placeholder="Bijv: Ontdek de 5 geheimen voor succesvolle marketing"
              />
              {subjectError && (
                <div className="absolute right-3 top-3">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                </div>
              )}
            </div>
            {subjectError ? (
              <div className="flex items-center gap-1 mt-1">
                <p className="text-sm text-red-600">{subjectError}</p>
              </div>
            ) : (
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-gray-500">
                  Houd het kort en pakkend, maximaal 60 karakters voor beste resultaten
                </p>
                <p className={`text-xs ${
                  subject.length > 60 ? 'text-amber-600 font-medium' : 'text-gray-400'
                }`}>
                  {subject.length} / 60
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Content (WYSIWYG)
            </label>
            <CampaignEditor
              content={content}
              onChange={setContent}
              onSaveNow={saveNow}
              onInsertFirstName={() => {
                setFirstNameInsertTarget('content');
                setShowFirstNameModal(true);
              }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Schrijf je email content. Gebruik de toolbar voor opmaak, links, en afbeeldingen
            </p>
          </div>

          {footerPreview && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Automatisch Toegevoegde Footer
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-xs text-gray-600 mb-3">
                  Deze footer wordt automatisch aan elke email toegevoegd (wettelijk verplicht):
                </p>
                <div
                  className="text-sm"
                  dangerouslySetInnerHTML={{ __html: footerPreview }}
                />
              </div>
            </div>
          )}

          {!hasBusinessData && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" />
                Email Footer
              </label>
              <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  Er wordt een footer toegevoegd met je bedrijfsgegevens. <a href="/dashboard/settings" className="text-[#FF6B35] underline">Vul eerst je adresgegevens in</a> om een preview te zien.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}
